import { ChangeEvent, Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { AIIcon, CalendarIcon, ChevronDownIcon, EditSquareIcon, InformationIcon, WarningIcon } from '../../../../../assets/icons';
import { formatDate, fromDatePickerFormat, toDatePickerFormat } from '../../../../../utils/dateHelpers';
import { DayValue } from '@amir04lm26/react-modern-calendar-date-picker';
import { useTextExtractor } from '../../../../../hooks/useTextExtractor';
import { PostJobStep } from '../../../../../store/workspace/types';
import MotionWrapper from '../../../../../layouts/MotionWrapper';
import CustomCalendar from '../../../../../components/Calendar';
import Modal from '../../../../../components/Modal';
import TextArea from '../../../../../components/TextArea';
import Spinner from '../../../../../components/Spinner';
import DropBox from '../../../../../containers/DropBox';
import Toggle from '../../../../../components/Toggle';
import Button from '../../../../../components/Button';
import styles from './style.module.scss';

interface Props {
    files: File[];
    deadline: DayValue;
    jobDescription: string;
    resumeMatching: boolean;
    fetchingJobDescription: boolean;
    getJobDescriptionFromAI: () => void;
    setStep: (step: PostJobStep) => void;
    removeFile: (fileName: string) => void;
    setDeadline: Dispatch<SetStateAction<DayValue>>;
    setJobDescription: Dispatch<SetStateAction<string>>;
    setResumeMatching: Dispatch<SetStateAction<boolean>>;
    fileSelect: (e: ChangeEvent<HTMLInputElement>) => void;
};

export default function ExamDeadline(props: Props) {
    const {
        files,
        setStep,
        deadline,
        removeFile,
        fileSelect,
        setDeadline,
        resumeMatching,
        setResumeMatching,
        jobDescription,
        setJobDescription,
        fetchingJobDescription,
        getJobDescriptionFromAI,
    } = props;

    const [isExpanded, setIsExpanded] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [showDeadlineModal, setShowDeadlineModal] = useState(false);
    const [tempDeadline, setTempDeadline] = useState<DayValue>(deadline);

    const openDeadlineModal = () => {
        setTempDeadline(deadline);
        setShowDeadlineModal(true);
    };

    const confirmDeadline = () => {
        setDeadline(tempDeadline);
        setShowDeadlineModal(false);
    };

    useEffect(() => {
        if (textareaRef.current) {
            setIsOverflowing(textareaRef.current.scrollHeight > textareaRef.current.clientHeight);
        }
    }, [jobDescription]);

    const expandTextarea = () => {
        if (!textareaRef.current) return;

        if (isExpanded) {
            textareaRef.current.style.height = `100%`;
            textareaRef.current.style.overflow = 'auto';
        } else {
            textareaRef.current.style.overflow = 'hidden';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }

        setIsExpanded(prev => !prev);
    };

    const {
        file,
        text,
        setFile,
        warning,
        extractText,
        handleFileChange,
    } = useTextExtractor(setJobDescription);

    return (
        <MotionWrapper className={styles.container}>
            <span className={styles.title}>
                Post new job - Exam deadline & job description
            </span>
            <div className={styles.activate}>
                <div className={styles.activate_title}>
                    <span>
                        The job matches candidates in our database.
                    </span>
                    <div className={styles.activate_title_info}>
                        <InformationIcon />
                        <div className={styles.activate_title_info_tooltip}>
                            If you would like to control who can take the exam from the job
                            link, please turn this function off. MeritFinder matches this job
                            with applicants with similar skills to the posted job in our database
                        </div>
                    </div>
                </div>
                <Toggle
                    toggled={resumeMatching}
                    setToggle={() => setResumeMatching(prev => !prev)}
                />
            </div>
            <div className={styles.deadline}>
                <span className={styles.deadline_label}>
                    Exam deadline: <b>*</b>
                </span>
                <div className={styles.deadline_box} onClick={openDeadlineModal}>
                    <span data-placeholder={!deadline}>
                        {deadline ? fromDatePickerFormat(deadline) : 'Select deadline'}
                    </span>
                    <CalendarIcon />
                </div>
                <div className={styles.deadline_warning}>
                    <WarningIcon />
                    <span>After this deadline, we will no longer accept new applicants.</span>
                </div>
            </div>
            <div className={styles.description}>
                <span className={styles.description_title}>Job description:</span>
                <div className={styles.description_AI}>
                    <span className={styles.description_AI_text}>
                        Generate job description using AI
                    </span>
                    {fetchingJobDescription ?
                        <div className={styles.description_AI_loading}>
                            <Spinner />
                        </div> :
                        <div
                            onClick={getJobDescriptionFromAI}
                            className={styles.description_AI_action}
                        >
                            <AIIcon />
                            <span>Generate by AI</span>
                        </div>}
                </div>
                <DropBox
                    file={file}
                    setFile={setFile}
                    uploading={false}
                    actionBtnTitle='Extract'
                    actionBtnFunc={extractText}
                    fileTypes={{ 'application/pdf': ['.pdf'], 'application/doc': ['.docx', '.txt'] }}
                    description=' It is optional for you to browse a job description file (.pdf, .docx or .txt)'
                />
                <div className={styles.description_text}>
                    <TextArea
                        ref={textareaRef}
                        maxLength={12000}
                        value={jobDescription}
                        placeholder='Job description'
                        onChange={setJobDescription}
                        className={styles.description_text_input}
                    />
                    {isOverflowing && (<div
                        onClick={expandTextarea}
                        data-is-expanded={isExpanded}
                        className={styles.description_text_more}
                    >
                        <ChevronDownIcon />
                        <span>Show {isExpanded ? 'less' : 'more'}</span>
                    </div>)}
                </div>
            </div>
            <div className={styles.actions}>
                <Button
                    text='Back'
                    type='Outline-Primary'
                    className={styles.actions_btn}
                    onClick={() => setStep('CodingExamDetail')}
                />
                <Button
                    type='Primary'
                    text='Continue'
                    className={styles.actions_btn}
                    onClick={() => setStep('Review')}
                    disabled={!deadline || !jobDescription || fetchingJobDescription}
                />
            </div>
            {showDeadlineModal && (
                <Modal
                    size='sm'
                    icon={<EditSquareIcon />}
                    title='Exam deadline'
                    childClassName={styles.deadline_modal}
                    closeFunc={() => setShowDeadlineModal(false)}
                >
                    <CustomCalendar
                        selectedDate={tempDeadline}
                        setSelectedDate={setTempDeadline}
                        minimumDate={toDatePickerFormat(formatDate(new Date())) || undefined}
                    />
                    <div className={styles.deadline_modal_actions}>
                        <Button
                            type='Outline-Dark'
                            text='Cancel'
                            className={styles.deadline_modal_actions_btn}
                            onClick={() => setShowDeadlineModal(false)}
                        />
                        <Button
                            type='Primary'
                            text='Confirm'
                            className={styles.deadline_modal_actions_btn}
                            onClick={confirmDeadline}
                            disabled={!tempDeadline}
                        />
                    </div>
                </Modal>
            )}
        </MotionWrapper>
    )
}
